import { createSession, logRecommendations } from "@/lib/analyticsDB";

export async function seedAnalyticsData(): Promise<boolean> {
  const req = indexedDB.open("stackmap-advisor-analytics");
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  const tx = db.transaction("sessions", "readonly");
  const countReq = tx.objectStore("sessions").count();
  const existingCount = await new Promise<number>((resolve) => {
    countReq.onsuccess = () => resolve(countReq.result as number);
  });
  db.close();

  if (existingCount > 0) return false; // already seeded

  const now = Date.now();
  const products = [
    "WorkTrack",
    "HCM",
    "PMS",
    "OMS",
    "Quality Audit",
    "Accounting",
    "Learning & Development",
    "RemoteOps Hub",
    "Admin",
    "Full Stack",
    "Growth & Marketing",
    "Sales & CRM",
    "Workplace Services",
    "Get Fit",
  ];

  const challenges = ["staff", "operations", "reporting", "billing", "performance", "marketing", "sales", "workplace"];
  const workModes = ["office", "remote", "field"];
  const teamSizes = ["1-10", "11-50", "51-200", "200+"];
  const industries = ["logistics", "retail", "manufacturing", "professional", "healthcare", "fitness", "construction", "other"];
  const complianceOptions = [
    ["quality-audits"],
    ["safety-inspections"],
    ["regulatory-reporting"],
    ["quality-audits", "safety-inspections"],
    ["all"],
  ];
  const locationOptions = ["single", "2-5", "6+"];
  const tools = [["excel"], ["whatsapp"], ["erp"], ["none"], ["excel", "whatsapp"], ["erp", "excel"]];

  const sessions: {
    id: string;
    openedAt: number;
    closedAt: number;
    lastStep: number;
    completed: boolean;
    submittedForm: boolean;
    demoBooked: boolean;
    trialStarted: boolean;
    products: string[];
    answers: Record<string, unknown>;
  }[] = [];

  // Generate 42 realistic sessions with varied drop-off points
  for (let i = 0; i < 42; i++) {
    const seed = i * 7;
    const industry = industries[seed % industries.length];
    const teamSize = teamSizes[seed % teamSizes.length];
    const workMode = workModes[seed % workModes.length];
    const challenge = challenges[seed % challenges.length];
    const currentTools = tools[seed % tools.length];
    const compAnswer = complianceOptions[seed % complianceOptions.length];
    const locAnswer = locationOptions[seed % locationOptions.length];

    // Simulate realistic drop-off
    const rand = (i * 31 + 17) % 100;
    let lastStep = 0;
    let completed = false;
    let submittedForm = false;
    let demoBooked = false;
    let trialStarted = false;
    let recs: string[] = [];

    if (rand > 5) lastStep = 1;
    if (rand > 15) lastStep = 2;
    if (rand > 30) lastStep = 3;
    if (rand > 45) lastStep = 4;

    // Conditional branches after work mode
    let hasComplianceBranch = industry === "healthcare" || industry === "manufacturing" || industry === "construction";
    let hasLocationBranch = workMode === "remote" || workMode === "field";

    // Determine branch insertion order
    // After industry (step 1) comes teamSize (step 2), then challenges (step 3), then workMode (step 4)
    // Compliance branch is inserted after industry (so it becomes step 2 if present)
    // Location branch is inserted after workMode
    // But our actual algorithm inserts after the question in the original order
    // Let me simplify: compliance after industry = becomes new step 2, pushing others
    // Actually the logic in advisorLogic.ts uses BASE_ORDER: industry, teamSize, challenges, workMode, currentTools
    // Conditional insertions happen AFTER each base question
    // So compliance (after industry) => between 1 and 2
    // Location (after workMode) => after step 4 (which is workMode)

    // Simplified step counting for seed data:
    // Base 5 questions = industry(1), teamSize(2), challenges(3), workMode(4), currentTools(5)
    // If compliance branch -> it appears between industry and teamSize? No, after industry.
    // Wait, BASE_ORDER is: industry, teamSize, challenges, workMode, currentTools
    // Conditional insertion: after 'industry', insert 'complianceNeeds' => [industry, complianceNeeds, teamSize, challenges, workMode, currentTools]
    // After 'workMode', insert 'locationCount' => [industry, complianceNeeds, teamSize, challenges, workMode, locationCount, currentTools]
    // So step numbers:
    // 1 = industry
    // 2 = complianceNeeds (if triggered) OR teamSize
    // 3 = teamSize (if compliance) OR challenges
    // etc.
    // results = totalSteps + 1
    // form = totalSteps + 2
    // success = totalSteps + 3

    // For seeding, let me compute lastStep properly
    let reachedStep = 1;
    if (rand > 5) reachedStep = 2; // Industry answered
    if (rand > 15) reachedStep = 3; // Next question
    if (rand > 30) reachedStep = 4;
    if (rand > 45) reachedStep = 5; // All base up to work mode
    if (rand > 55) reachedStep = 6; // Work mode answered, triggers location branch if applicable

    // After work mode (step 5), locationCount may appear as step 6 for remote/field
    // Then currentTools becomes step 6 or 7
    let totalSteps = 5;
    if (hasComplianceBranch) totalSteps = 6;
    if (hasLocationBranch) totalSteps = hasComplianceBranch ? 7 : 6;

    if (rand > 65) {
      reachedStep = hasComplianceBranch ? 6 : 5; // compliance answered OR work mode done
      if (hasLocationBranch && rand > 70) {
        reachedStep = hasComplianceBranch ? 7 : 6; // location answered
      }
      if (rand > 75) {
        reachedStep = totalSteps; // current tools answered
      }
      if (rand > 80) {
        lastStep = totalSteps + 1; // results
        completed = true;
        recs = getProductsFor(challenge, workMode, industry, compAnswer, locAnswer, teamSize);
      }
      if (rand > 85) {
        lastStep = totalSteps + 2; // form
      }
      if (rand > 90) {
        lastStep = totalSteps + 3; // submitted
        submittedForm = true;
        demoBooked = true;
      }
      if (rand > 95) {
        trialStarted = true;
      }
    }

    // Actually let me just use the simpler lastStep approach that maps to steps in the new system
    // The panel uses: resultsStep = totalSteps + 1, formStep = totalSteps + 2, successStep = totalSteps + 3
    // But getFunnelData in analyticsDB just checks lastStep >= N for Q1-Q6
    // So let me just set lastStep as a raw step number

    // Recompute simply:
    let step = 1; // at least industry
    if (rand > 15) {
      // passed industry
      step = 2;
      if (hasComplianceBranch) {
        // compliance question at step 2
        if (rand > 25) step = 3; // compliance answered
      }
    }
    if ((hasComplianceBranch && step >= 3) || (!hasComplianceBranch && step >= 2)) {
      if (rand > 30) step = hasComplianceBranch ? 4 : 3; // teamSize
    }
    if (step >= (hasComplianceBranch ? 4 : 3)) {
      if (rand > 40) step = hasComplianceBranch ? 5 : 4; // challenges
    }
    if (step >= (hasComplianceBranch ? 5 : 4)) {
      if (rand > 50) step = hasComplianceBranch ? 6 : 5; // workMode
    }
    if (step >= (hasComplianceBranch ? 6 : 5)) {
      if (hasLocationBranch) {
        if (rand > 60) step = hasComplianceBranch ? 7 : 6; // locationCount
      }
    }
    if (step >= (hasLocationBranch ? (hasComplianceBranch ? 7 : 6) : (hasComplianceBranch ? 6 : 5))) {
      if (rand > 70) step = totalSteps; // currentTools
    }
    if (rand > 80) {
      lastStep = totalSteps + 1; // results
      completed = true;
      recs = getProductsFor(challenge, workMode, industry, compAnswer, locAnswer, teamSize);
    }
    if (rand > 85) lastStep = totalSteps + 2; // form
    if (rand > 90) {
      lastStep = totalSteps + 3;
      submittedForm = true;
      demoBooked = true;
    }
    if (rand > 95) trialStarted = true;

    // Fallback if nothing set it past step 1
    lastStep = Math.max(step, lastStep);

    const openedAt = now - (seed * 3600000) - Math.floor(Math.random() * 600000);
    const duration = lastStep <= 1 ? 8000 : lastStep * 12000 + Math.floor(Math.random() * 25000);

    const answers: Record<string, unknown> = {
      industry,
      teamSize,
      challenges: [challenge, challenges[(seed + 2) % challenges.length]],
      workMode,
      currentTools,
    };

    if (hasComplianceBranch && step >= 3) {
      answers.complianceNeeds = compAnswer;
    }
    if (hasLocationBranch && step >= (hasComplianceBranch ? 7 : 6)) {
      answers.locationCount = locAnswer;
    }

    sessions.push({
      id: `demo-${i}-${Date.now()}-${seed}`,
      openedAt,
      closedAt: openedAt + duration,
      lastStep,
      completed,
      submittedForm,
      demoBooked,
      trialStarted,
      products: recs,
      answers,
    });
  }

  // Helper to get products based on inputs
  function getProductsFor(
    challenge: string,
    workMode: string,
    industry: string,
    compliance: string[],
    location: string,
    teamSize: string
  ): string[] {
    const set = new Set<string>();

    switch (challenge) {
      case "staff": set.add("HCM"); set.add("WorkTrack"); set.add("Get Fit"); break;
      case "operations": set.add("WorkTrack"); set.add("OMS"); set.add("Quality Audit"); break;
      case "reporting": set.add("PMS"); set.add("RemoteOps Hub"); break;
      case "billing": set.add("Accounting"); set.add("OMS"); break;
      case "performance": set.add("PMS"); set.add("HCM"); break;
      case "workplace": set.add("Workplace Services"); set.add("Admin"); break;
      case "marketing": set.add("Growth & Marketing"); set.add("Sales & CRM"); break;
      case "sales": set.add("Sales & CRM"); set.add("Growth & Marketing"); break;
    }

    if (workMode === "field") { set.add("WorkTrack"); set.add("RemoteOps Hub"); }
    if (workMode === "remote") { set.add("RemoteOps Hub"); set.add("WorkTrack"); set.add("HCM"); }
    if (workMode === "office") { set.add("HCM"); set.add("PMS"); set.add("Get Fit"); }

    if (industry === "logistics") { set.add("OMS"); set.add("Quality Audit"); set.add("WorkTrack"); }
    if (industry === "retail") { set.add("OMS"); set.add("Accounting"); }
    if (industry === "manufacturing") { set.add("Quality Audit"); set.add("WorkTrack"); }
    if (industry === "construction") { set.add("WorkTrack"); set.add("RemoteOps Hub"); }
    if (industry === "healthcare") { set.add("HCM"); set.add("Learning & Development"); }
    if (industry === "fitness") { set.add("Get Fit"); set.add("HCM"); set.add("Growth & Marketing"); }
    if (industry === "professional") { set.add("PMS"); set.add("Accounting"); set.add("WorkTrack"); }

    if (compliance.includes("quality-audits") || compliance.includes("all")) set.add("Quality Audit");
    if (compliance.includes("safety-inspections") || compliance.includes("all")) set.add("Quality Audit");
    if (compliance.includes("regulatory-reporting") || compliance.includes("all")) { set.add("Admin"); set.add("Quality Audit"); }

    if (location === "2-5" || location === "6+") set.add("RemoteOps Hub");
    if (location === "6+") set.add("Admin");

    const isLarge = teamSize === "51-200" || teamSize === "200+";
    const hasMany = set.size >= 4;
    if (isLarge && hasMany) return ["Full Stack"];
    return Array.from(set).slice(0, 3);
  }

  // Seed into DB
  for (const s of sessions) {
    await createSession({
      id: s.id,
      pagePath: ["/", "/products", "/solutions", "/bespoke", "/pricing"][Math.floor(Math.random() * 5)],
      openedAt: s.openedAt,
      closedAt: s.closedAt,
      lastStep: s.lastStep,
      completed: s.completed,
      submittedForm: s.submittedForm,
      demoBooked: s.demoBooked,
      trialStarted: s.trialStarted,
      productsRecommended: s.products,
      answers: s.answers,
    });
    if (s.products.length > 0) {
      await logRecommendations(
        s.products.map((p) => ({ sessionId: s.id, product: p, timestamp: s.openedAt }))
      );
    }
  }

  return true;
}